import { Box, Stack } from '@mui/material';
import { compact, keyBy, uniq } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { ButtonGroup } from '@/components/form/baseInputs/ButtonGroup';
import { FileSearch02Icon } from '@/components/icons/FileSearch02Icon';
import { ActionCard } from '@/components/layout/ActionCard/ActionCard';
import { Card } from '@/components/layout/Card/Card';
import { Markdown } from '@/components/Markdown/Markdown';
import { ChatDocumentReferencesBar } from '@/modules/common/Chat/ChatSideContent/documentViewer/ChatDocumentReferencesBar';
import { DocumentRenderError } from '@/modules/documents/components/HouseholdDocumentsViewer/DocumentRenderError';
import { DocumentEmptyStateSlot } from '@/modules/documents/components/HouseholdDocumentsViewer/DocumentViewerTabEmptyState';
import {
  DocumentDetailsViewOptions,
  getDocumentDetailsViewButtonOptions,
} from '@/modules/documents/documents.constants';
import { PDFViewerLazy } from '@/modules/pdf/PDFViewer';
import { useAnnotatedPDFViewerContext } from '@/modules/pdf/PDFViewer/AnnotatedPDFViewer/context/AnnotatedPDFViewer.context';
import { DocumentChromeProps } from '@/modules/pdf/PDFViewer/DocumentChrome';
import { PDFZoomControls } from '@/modules/pdf/PDFViewer/PDFZoomControls';
import { COLORS } from '@/styles/tokens/colors';

import {
  CitationDocumentLoader,
  CitationDocumentState,
} from './CitationDocumentLoader';

interface ChatDocumentViewerProps {
  /** Which tab the viewer opens on: citations land on the document, document
   *  summary references land on the summary. */
  initialTab?: DocumentDetailsViewOptions;
  /**
   * Summary markdown to show on the Summary tab in place of the active
   * document's own first summary — the document-summary side content knows
   * exactly which summary was referenced, which is not necessarily the
   * document's first.
   */
  summaryOverride?: string | null;
}

/**
 * The Summary/Document toggle in the chat panel design leads with Summary;
 * the shared options (used by the document details page) lead with Document.
 */
function getChatViewButtonOptions() {
  const optionsByValue = keyBy(getDocumentDetailsViewButtonOptions(), 'value');
  return compact([optionsByValue.summary, optionsByValue.document]);
}

/**
 * The chat panel variant of the document chrome: the scroll container for the
 * pages. No page shadow — the panel's own chrome frames the document.
 */
function ChatDocumentChrome({
  children,
  shouldCenterContent,
  scrollContainerRef,
  sx = {},
}: DocumentChromeProps) {
  return (
    <Box
      ref={scrollContainerRef}
      data-name="ChatDocumentChrome"
      sx={{
        height: '100%',
        width: '100%',
        overflowX: 'auto',
        overflowY: 'auto',
        ...sx,
      }}
    >
      {/* The annotation overlays position against this relative box, so it
          must stay unpadded — padding here would offset them from the page
          canvases. minHeight (not height) lets the box grow with the pages. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: shouldCenterContent ? 'center' : 'start',
          minHeight: '100%',
          position: 'relative',
          width: '100%',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function SummaryEmptyState() {
  return (
    <Stack
      data-testid="chat-document-viewer-summary-empty"
      justifyContent="center"
      height="100%"
      width="100%"
      p={6}
    >
      <ActionCard
        variant="noCard"
        heading="No summary available"
        description="This document doesn't have a summary yet"
        sx={{ backgroundColor: 'transparent !important' }}
        icon={<FileSearch02Icon size={40} sx={{ color: COLORS.GRAY[300] }} />}
      />
    </Stack>
  );
}

/**
 * Renders one document in the chat side content panel: a toolbar row (the
 * Summary/Document view toggle and, on the Document tab, the zoom controls)
 * fixed below the panel header, above the scrollable content — a Document tab
 * (the PDF, with any cited passages highlighted) and a Summary tab (the
 * document's AI summary as markdown). The document id, annotations, and
 * focused-passage index all come from the surrounding
 * AnnotatedPDFViewerProvider (seeded by the clicked citation or the referenced
 * summary's parent document); the ChatDocumentReferencesBar footer lets the
 * reader step between passages.
 *
 * A single citation can resolve to passages spanning multiple documents, so the
 * viewer follows the active annotation's document rather than a fixed one:
 * stepping to a passage in another document moves the active annotation, and the
 * displayed document is derived from it. To keep that switch instant, every
 * referenced document is prefetched up front by a CitationDocumentLoader per
 * documentId; this component is a pure renderer over the resolved states and
 * only shows a loading state until the *active* document's state first arrives.
 *
 * Deliberately lighter than HouseholdDocumentsViewer: there is no document
 * selector or precedence control — just the document, its highlights, and its
 * summary.
 */
export function ChatDocumentViewer({
  initialTab = 'document',
  summaryOverride = null,
}: ChatDocumentViewerProps) {
  const { documentId, annotations, activeAnnotationIndex } =
    useAnnotatedPDFViewerContext();

  const [activeTab, setActiveTab] =
    useState<DocumentDetailsViewOptions>(initialTab);

  // Summaries can contain citation links of their own; clicking one resolves
  // annotations into the surrounding provider, but the highlighted passages
  // render on the PDF — jump to the Document tab so they're visible. The
  // active index is a dependency because re-clicking the same citation just
  // cycles the active passage without replacing the annotations. Skips the
  // mount pass so snapshot-seeded annotations (citation side content opens
  // with them) don't override initialTab.
  useUpdateEffect(() => {
    if (annotations?.length) {
      setActiveTab('document');
    }
  }, [annotations, activeAnnotationIndex]);

  // The displayed document must follow the *active annotation*, not the
  // provider's `documentId`. That context value falls back to a retained
  // `overrideDocumentId`, which goes stale when a new citation reuses this
  // provider in place (see ChatCitationSideContentHost) and would otherwise
  // leave the viewer on the previous citation's document. Deriving it from the
  // active annotation keeps the document correct however focus changes; fall
  // back to the base id only when there are no annotations.
  const activeDocumentId =
    annotations?.[activeAnnotationIndex]?.documentID ?? documentId;

  // Resolved URL + page geometry for every document this citation references,
  // keyed by documentId and filled by the CitationDocumentLoaders below.
  const [documentStates, setDocumentStates] = useState<
    Map<string, CitationDocumentState>
  >(new Map());

  const handleStateChange = useCallback(
    (id: string, state: CitationDocumentState) => {
      setDocumentStates((prev) => {
        const next = new Map(prev);
        next.set(id, state);
        return next;
      });
    },
    []
  );

  const prefetchDocumentIds = useMemo(() => {
    const ids: string[] = [];
    // The active document is always one of the annotated documents, but include
    // it explicitly so the viewer still resolves if annotations are empty.
    if (activeDocumentId) {
      ids.push(activeDocumentId);
    }
    annotations?.forEach((annotation) => {
      if (annotation.documentID) {
        ids.push(annotation.documentID);
      }
    });
    // Dedupe so each referenced document mounts exactly one loader (and gives
    // the .map() below stable, collision-free React keys).
    return uniq(ids);
  }, [activeDocumentId, annotations]);

  // Open at fit-to-width (1.0) so the full page width is visible; the focused
  // passage is still highlighted and scrolled into view by the annotation
  // overlay. The reader can zoom in manually via PDFZoomControls.
  const [zoomLevel, setZoomLevel] = useState(1.0);

  const activeState = activeDocumentId
    ? documentStates.get(activeDocumentId)
    : undefined;
  const source = activeState?.source ?? null;
  const filePages = activeState?.filePages ?? [];
  // Show loading only until the active document's state first arrives. Documents
  // reached by stepping between passages are already prefetched, so their state
  // is present and switching to them shows no loading indicator.
  const loading = !activeState || activeState.loading;
  const error = activeState?.error ?? false;

  const summary = summaryOverride ?? activeState?.summary ?? null;

  return (
    <Card
      data-testid="chat-document-viewer"
      // No border or shadow — the panel's own chrome frames the viewer.
      variant="transparent"
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        px: 2,
        pb: 2,
        overflow: 'hidden',
        borderRadius: 0,
      }}
    >
      {prefetchDocumentIds.map((id) => (
        <CitationDocumentLoader
          key={id}
          documentId={id}
          onStateChange={handleStateChange}
        />
      ))}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        sx={{ flexShrink: 0, py: 2 }}
      >
        <ButtonGroup
          label="Choose view type"
          hideLabel
          value={activeTab}
          options={getChatViewButtonOptions()}
          onChange={(_, value) => setActiveTab(value)}
        />
        {activeTab === 'document' && (
          <PDFZoomControls
            onZoomLevelChange={setZoomLevel}
            externalZoomLevel={zoomLevel}
          />
        )}
      </Stack>
      {activeTab === 'document' && (
        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
          <PDFViewerLazy
            // Remount the viewer when the document changes so internal page
            // state resets cleanly if the reader steps to a passage in another doc.
            key={activeDocumentId ?? 'empty'}
            loading={loading}
            error={error}
            documentId={activeDocumentId}
            source={source}
            annotations={annotations}
            activeAnnotationIdx={activeAnnotationIndex}
            zoomLevel={zoomLevel}
            filePages={filePages}
            slots={{
              DocumentChrome: ChatDocumentChrome,
              DocumentEmptyState: DocumentEmptyStateSlot,
              DocumentFooter: ChatDocumentReferencesBar,
              DocumentRenderError: DocumentRenderError,
            }}
          />
        </Box>
      )}
      {activeTab === 'summary' &&
        (summary ? (
          <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', pb: 3 }}>
            <Card
              variant="transparent"
              sx={{
                margin: '0 auto',
              }}
            >
              <Markdown markdown={summary} />
            </Card>
          </Box>
        ) : (
          <SummaryEmptyState />
        ))}
    </Card>
  );
}
